import React from 'react'
import { Paper, Grid, Text, Badge, Group, Title, Stack, Card, Alert } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { useAppTranslation } from 'hooks/useTranslation'
import { CampaignEngagementStats, PlatformCountStat } from 'supabase/campaigns/analytics'
import {
  getCreativeLanguageGaps,
  isTargetingField,
  targetingLabel,
  TARGETING_FIELD_LABEL_KEYS
} from 'supabase/campaigns/targeting'
import { CampaignStatusBadge } from './CampaignStatusBadge'

interface CampaignDetailViewProps {
  campaign: any // Will be CampaignWithAdvertiser & { campaign_creatives: any[] }
  stats?: CampaignEngagementStats | null
}

const EMPTY_STAT: PlatformCountStat = {
  total: 0,
  unique: 0,
  byPlatform: { mobile: { total: 0, unique: 0 }, web: { total: 0, unique: 0 } }
}

// Click types surfaced in the Ad Performance panel, in display order. Keys match
// the ad_clicks.click_type CHECK constraint; labels resolve via i18n.
const CLICK_STAT_ROWS: { key: string; labelKey: string }[] = [
  { key: 'open_detail', labelKey: 'campaigns.stat_open_detail' },
  { key: 'whatsapp', labelKey: 'campaigns.stat_whatsapp' },
  { key: 'call', labelKey: 'campaigns.stat_call' },
  { key: 'website', labelKey: 'campaigns.stat_website' },
  { key: 'instagram', labelKey: 'campaigns.stat_instagram' },
  { key: 'promo_copy', labelKey: 'campaigns.stat_promo_copy' },
  { key: 'directions', labelKey: 'campaigns.stat_directions' }
]

// One "total (unique)" tile, with the mobile/web split underneath.
//
// The split deliberately shows totals only. Web clicks are logged by a server
// route that carries no user_id or session_id (see web/src/lib/adSession.ts), so
// a per-platform unique count would read a permanent, misleading 0 for every
// web click row.
function StatTile({ label, stat }: { label: string; stat: PlatformCountStat }) {
  const { t } = useAppTranslation()

  return (
    <Grid.Col span={{ base: 6, sm: 4, md: 3 }}>
      <Text size="sm" c="dimmed">{label}</Text>
      <Text fw={500} size="lg" c="blue">
        {stat.total.toLocaleString()}{' '}
        <Text span size="sm" c="dimmed">({stat.unique.toLocaleString()})</Text>
      </Text>
      <Text size="xs" c="dimmed">
        {t('campaigns.platform_mobile') || 'Mobile'} {stat.byPlatform.mobile.total.toLocaleString()}
        {' · '}
        {t('campaigns.platform_web') || 'Web'} {stat.byPlatform.web.total.toLocaleString()}
      </Text>
    </Grid.Col>
  )
}

export function CampaignDetailView({ campaign, stats = null }: CampaignDetailViewProps) {
  const { t } = useAppTranslation()

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  // status_history entries come from two writers with different field names:
  // the DB trigger uses `changed_at` (for both status and targeting entries),
  // manual status updates use `timestamp`. Accept either and avoid rendering
  // "Invalid Date" for missing/bad values.
  const formatHistoryTime = (history: any) => {
    const raw = history?.timestamp || history?.changed_at
    const date = raw ? new Date(raw) : null
    return date && !isNaN(date.getTime()) ? date.toLocaleString() : '—'
  }

  // D-09/D-10: assigned creatives with no title/description in the targeted
  // language. Display-only; nothing is blocked (spec D12).
  const languageGaps = getCreativeLanguageGaps(campaign)

  // D-07: targeting entries read as one labelled row; any other `field` renders
  // raw; entries without `field` (status changes) keep the Badge → Badge look.
  const renderHistoryChange = (history: any) => {
    const field: unknown = history.field
    if (isTargetingField(field)) {
      return (
        <Text>
          {t('campaigns.history_field_change', {
            field: t(TARGETING_FIELD_LABEL_KEYS[field]),
            from: targetingLabel(field, history.from, t),
            to: targetingLabel(field, history.to, t)
          })}
        </Text>
      )
    }

    if (typeof field === 'string' && field !== '') {
      return (
        <Text>
          {t('campaigns.history_field_change', {
            field,
            from: history.from ?? '',
            to: history.to ?? ''
          })}
        </Text>
      )
    }

    return (
      <Group>
        <Badge variant="light">{history.from}</Badge>
        <Text>→</Text>
        <Badge variant="filled">{history.to}</Badge>
      </Group>
    )
  }

  return (
    <Stack gap="lg">
      {languageGaps.length > 0 && (
        <Alert
          icon={<IconAlertCircle size="1rem" />}
          color="yellow"
          title={t('campaigns.creative_language_warning_title')}
        >
          <Text size="sm">{t('campaigns.creative_language_warning')}</Text>
          {languageGaps.map((gap, index) => (
            // A creative can be assigned twice (no unique constraint), so creativeId alone may repeat
            <Text size="sm" key={`${gap.creativeId}-${index}`}>
              {gap.title || t('creatives.untitled')}: {gap.missing.map((f) => t(`campaigns.missing_field_${f}`)).join(', ')}
            </Text>
          ))}
        </Alert>
      )}

      {/* Basic Information */}
      <Paper p="lg" shadow="sm">
        <Title order={4} mb="md">{t('campaigns.basic_information') || 'Basic Information'}</Title>
        <Grid>
          <Grid.Col span={6}>
            <Text size="sm" c="dimmed">{t('campaigns.name')}</Text>
            <Text fw={500}>{campaign.name}</Text>
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" c="dimmed">{t('campaigns.status_column')}</Text>
            <CampaignStatusBadge status={campaign.status} />
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" c="dimmed">{t('campaigns.advertiser')}</Text>
            <Text fw={500}>{campaign.advertisers?.name || '-'}</Text>
            <Text size="xs" c="dimmed">{campaign.advertisers?.category}</Text>
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" c="dimmed">{t('campaigns.contact_email') || 'Contact Email'}</Text>
            <Text fw={500}>{campaign.advertisers?.contact_email || '-'}</Text>
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" c="dimmed">{t('campaigns.tier')}</Text>
            <Badge size="lg" variant="outline">
              {campaign.tier}
            </Badge>
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" c="dimmed">{t('campaigns.language_target')}</Text>
            <Badge size="lg" variant="outline">
              {targetingLabel('language_target', campaign.language_target, t)}
            </Badge>
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" c="dimmed">{t('campaigns.platform_target')}</Text>
            <Badge size="lg" variant="outline">
              {targetingLabel('platform_target', campaign.platform_target, t)}
            </Badge>
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" c="dimmed">{t('campaigns.start_date')}</Text>
            <Text fw={500}>{formatDate(campaign.start_date)}</Text>
          </Grid.Col>

          <Grid.Col span={6}>
            <Text size="sm" c="dimmed">{t('campaigns.end_date')}</Text>
            <Text fw={500}>{formatDate(campaign.end_date)}</Text>
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Ad Performance */}
      <Paper p="lg" shadow="sm">
        <Group justify="space-between" align="baseline" mb="md">
          <Title order={4}>{t('campaigns.ad_performance') || 'Ad Performance'}</Title>
          <Text size="xs" c="dimmed" ta="right">
            {t('campaigns.stat_unique_hint') || 'Total (unique users)'}
            <br />
            {t('campaigns.stat_platform_hint') || 'Per-platform figures are totals'}
          </Text>
        </Group>
        <Grid>
          <StatTile label={t('campaigns.impressions')} stat={stats?.impressions ?? EMPTY_STAT} />
          {CLICK_STAT_ROWS.map(({ key, labelKey }) => (
            <StatTile
              key={key}
              label={t(labelKey) || key}
              stat={stats?.clicksByType?.[key] ?? EMPTY_STAT}
            />
          ))}
        </Grid>
      </Paper>

      {/* Creative Assignments */}
      <Paper p="lg" shadow="sm">
        <Title order={4} mb="md">{t('campaigns.creative_assignments') || 'Creative Assignments'}</Title>
        {campaign.campaign_creatives && campaign.campaign_creatives.length > 0 ? (
          <Grid>
            {campaign.campaign_creatives.map((assignment: any) => (
              <Grid.Col span={4} key={assignment.id}>
                <Card shadow="sm" padding="sm" withBorder>
                  <Card.Section>
                    {assignment.creatives?.card_image_url ? (
                      <img
                        src={assignment.creatives.card_image_url}
                        alt={assignment.creatives.title || 'Creative'}
                        style={{ width: '100%', height: '200px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div style={{ width: '100%', height: '200px', background: '#f1f3f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Text c="dimmed">{t('common.no_preview') || 'No preview'}</Text>
                      </div>
                    )}
                  </Card.Section>
                  <Group justify="apart" mt="md" mb="xs">
                    <Text fw={500}>{assignment.creatives?.title || 'Untitled Creative'}</Text>
                  </Group>
                  {assignment.creatives?.description && (
                    <Text size="xs" c="dimmed" mt="xs">
                      {assignment.creatives.description}
                    </Text>
                  )}
                  {assignment.creatives?.cta_text && (
                    <Badge mt="xs" variant="light" color="blue">
                      {assignment.creatives.cta_text}
                    </Badge>
                  )}
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        ) : (
          <Text c="dimmed">{t('campaigns.no_creatives_assigned') || 'No creatives assigned'}</Text>
        )}
      </Paper>

      {/* History (status and targeting changes) */}
      {campaign.status_history && campaign.status_history.length > 0 && (
        <Paper p="lg" shadow="sm">
          <Title order={4} mb="md">{t('campaigns.status_history') || 'History'}</Title>
          <Stack gap="sm">
            {campaign.status_history.map((history: any, index: number) => (
              <Group key={index} justify="apart">
                {renderHistoryChange(history)}
                <Text size="sm" c="dimmed">
                  {formatHistoryTime(history)}
                </Text>
              </Group>
            ))}
          </Stack>
        </Paper>
      )}

      {/* Pricing Information (if available) */}
      {(campaign.base_price || campaign.final_price) && (
        <Paper p="lg" shadow="sm">
          <Title order={4} mb="md">{t('campaigns.pricing_information') || 'Pricing Information'}</Title>
          <Grid>
            {campaign.base_price && (
              <Grid.Col span={4}>
                <Text size="sm" c="dimmed">{t('campaigns.base_price') || 'Base Price'}</Text>
                <Text fw={500}>€{campaign.base_price}</Text>
              </Grid.Col>
            )}
            {campaign.discount_percentage && (
              <Grid.Col span={4}>
                <Text size="sm" c="dimmed">{t('campaigns.discount') || 'Discount'}</Text>
                <Text fw={500}>{campaign.discount_percentage}%</Text>
              </Grid.Col>
            )}
            {campaign.final_price && (
              <Grid.Col span={4}>
                <Text size="sm" c="dimmed">{t('campaigns.final_price') || 'Final Price'}</Text>
                <Text fw={500} size="lg" c="green">€{campaign.final_price}</Text>
              </Grid.Col>
            )}
          </Grid>
        </Paper>
      )}
    </Stack>
  )
}